// /parent/pickup — manage authorised pickup people for each child in
// the family. Mobile-first card list; each card opens an edit sheet.
//
// Parents (legal guardians via family_members) are implicitly
// authorised — this list is for OTHER trusted adults (grandparents,
// aunts, neighbors, sitters, regular carpoolers).

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Phone, ShieldCheck, Pencil, TrashSimple, UserCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { listChildrenForParent } from "@/lib/dailyReports";
import {
  deactivate,
  listForChild,
} from "@/lib/pickupAuthorizations";
import ChildAvatar from "@/components/children/ChildAvatar";
import PickupPersonSheet from "@/components/pickup/PickupPersonSheet";
import PickupPhoto from "@/components/pickup/PickupPhoto";
import PickupHistoryCard from "@/components/pickup/PickupHistoryCard";

export default function ParentPickup() {
  const { profile } = useAuth();
  const [children, setChildren] = useState([]);
  const [byChild, setByChild] = useState({}); // childId -> list
  const [loading, setLoading] = useState(true);
  const [sheet, setSheet] = useState({ open: false, child: null, existing: null });

  const refresh = async () => {
    if (!profile?.id) return;
    setLoading(true);
    try {
      const kids = await listChildrenForParent(profile.id);
      setChildren(kids || []);
      const byKid = {};
      await Promise.all(
        (kids || []).map(async (c) => {
          byKid[c.id] = await listForChild(c.id);
        }),
      );
      setByChild(byKid);
    } catch (err) {
      toast.error(err.message || "Could not load pickup list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  const onAdd = (child) =>
    setSheet({ open: true, child, existing: null });

  const onEdit = (child, row) =>
    setSheet({ open: true, child, existing: row });

  const onRemove = async (row) => {
    if (
      !window.confirm(
        `Remove ${row.full_name} from the pickup list? You can add them back later.`,
      )
    )
      return;
    try {
      await deactivate(row.id);
      toast.success(`${row.full_name} removed`);
      refresh();
    } catch (err) {
      toast.error(err.message || "Could not remove");
    }
  };

  return (
    <div data-testid="parent-pickup" className="space-y-5">
      <div>
        <Link
          to="/parent"
          data-testid="parent-pickup-back"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900"
        >
          <ArrowLeft size={14} weight="bold" />
          Back to Home
        </Link>
        <h1 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
          Authorised pickup
        </h1>
        <p className="mt-2 max-w-xl text-neutral-600">
          People — beyond you and your co-parent — who are allowed to
          collect your child. Teachers see this list at the door and
          will check the photo and PIN if set.
        </p>
      </div>

      {loading && (
        <p className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50/30 px-3 py-6 text-center text-sm text-neutral-500">
          Loading…
        </p>
      )}

      {!loading && children.length === 0 && (
        <p className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50/30 px-3 py-6 text-center text-sm text-neutral-500">
          No children linked to your family yet.
        </p>
      )}

      {!loading && children.length > 0 && (
        <PickupHistoryCard children={children} limit={20} />
      )}

      <ul className="space-y-5" data-testid="parent-pickup-children">
        {children.map((child) => (
          <li
            key={child.id}
            data-testid={`parent-pickup-child-${child.id}`}
            className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-[0_2px_12px_rgba(20,22,26,0.04)] sm:p-5"
          >
            <header className="flex items-center gap-3">
              <ChildAvatar
                path={child.profile_photo_path}
                name={`${child.first_name || ""} ${child.last_name || ""}`}
                size={40}
              />
              <div className="min-w-0 flex-1">
                <p className="font-heading text-base font-semibold tracking-tight text-neutral-900">
                  {child.first_name} {child.last_name || ""}
                </p>
                <p className="text-[11px] text-neutral-500">
                  {(byChild[child.id] || []).length} authorised
                </p>
              </div>
              <button
                type="button"
                data-testid={`parent-pickup-add-${child.id}`}
                onClick={() => onAdd(child)}
                className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-primary-hover"
              >
                <Plus size={12} weight="bold" />
                Add
              </button>
            </header>

            {(byChild[child.id] || []).length === 0 ? (
              <p className="mt-3 rounded-2xl border border-dashed border-neutral-200 bg-neutral-50/30 px-3 py-3 text-center text-[12px] text-neutral-500">
                Just you and your co-parent for now.
              </p>
            ) : (
              <ul className="mt-3 space-y-2">
                {(byChild[child.id] || []).map((row) => (
                  <li
                    key={row.id}
                    data-testid={`parent-pickup-row-${row.id}`}
                    className="flex items-start gap-3 rounded-2xl border border-neutral-200 bg-white p-3"
                  >
                    <PickupPhoto
                      path={row.photo_path}
                      name={row.full_name}
                      size={40}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="flex flex-wrap items-center gap-1 text-sm font-semibold text-neutral-900">
                        {row.full_name}
                        {row.is_emergency_contact && (
                          <span className="rounded-full bg-[#C6851E]/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#8A5A12]">
                            Emergency
                          </span>
                        )}
                      </p>
                      <p className="mt-0.5 text-[12px] text-neutral-600">
                        {row.relationship || "—"}
                        {row.phone ? ` · ${row.phone}` : ""}
                      </p>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {row.phone && (
                          <a
                            href={`tel:${row.phone}`}
                            className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-neutral-700 hover:bg-neutral-50"
                          >
                            <Phone size={10} weight="duotone" />
                            Call
                          </a>
                        )}
                        {row.pin && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                            <ShieldCheck size={10} weight="duotone" />
                            PIN set
                          </span>
                        )}
                      </div>
                      {row.notes && (
                        <p className="mt-1 text-[11px] italic text-neutral-500">
                          "{row.notes}"
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <button
                        type="button"
                        data-testid={`parent-pickup-edit-${row.id}`}
                        onClick={() => onEdit(child, row)}
                        className="rounded-full border border-neutral-200 bg-white px-2 py-1 text-[10px] font-semibold text-neutral-700 hover:bg-neutral-50"
                      >
                        <Pencil size={10} weight="bold" className="inline" />
                      </button>
                      <button
                        type="button"
                        data-testid={`parent-pickup-remove-${row.id}`}
                        onClick={() => onRemove(row)}
                        className="rounded-full border border-neutral-200 bg-white px-2 py-1 text-[10px] font-semibold text-[#8A5A12] hover:bg-[#C6851E]/5"
                      >
                        <TrashSimple size={10} weight="bold" className="inline" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>

      <PickupPersonSheet
        open={sheet.open}
        onOpenChange={(o) => setSheet((s) => ({ ...s, open: o }))}
        child={sheet.child}
        existing={sheet.existing}
        addedBy={profile?.id}
        onSaved={() => refresh()}
      />

      <p className="rounded-2xl border border-neutral-200 bg-neutral-50/40 px-3 py-3 text-[11px] text-neutral-500">
        <UserCircle size={12} weight="duotone" className="mr-1 inline align-text-top" />
        You and your co-parent (the legal guardians on file) are always
        authorised — no need to add yourselves.
      </p>
    </div>
  );
}
